import { Component, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { isChunkLoadError, reloadForStaleChunk, willAutoReload } from '@/lib/chunkReload';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  componentStack: string | null;
  reloading: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null, reloading: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error, componentStack: null, reloading: isChunkLoadError(error) && willAutoReload() };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error.message, error.stack, info.componentStack);
    const reloading = isChunkLoadError(error) && reloadForStaleChunk();
    this.setState({ componentStack: info.componentStack ?? null, reloading });
  }

  render() {
    if (this.state.reloading) {
      return (
        <div className="flex min-h-[400px] items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          Carregando a versão mais recente do sistema…
        </div>
      );
    }
    if (this.state.error) {
      const chunkError = isChunkLoadError(this.state.error);
      return (
        <div className="flex min-h-[400px] flex-col items-center justify-center gap-4 p-8 text-center">
          <h2 className="text-xl font-semibold">Algo deu errado</h2>
          <p className="max-w-md text-sm text-muted-foreground">
            {chunkError
              ? 'O sistema foi atualizado e esta tela não carregou. Clique em Tentar novamente para recarregar a página.'
              : this.state.error.message}
          </p>
          <details className="max-w-2xl text-left">
            <summary className="cursor-pointer text-xs text-muted-foreground">
              Detalhes técnicos
            </summary>
            <pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap rounded bg-muted p-3 text-[11px] leading-relaxed">
              {chunkError && `${this.state.error.message}\n`}
              {this.state.error.stack}
              {this.state.componentStack}
            </pre>
          </details>
          <Button
            onClick={() =>
              // Chunk ausente não se resolve re-renderizando: o React.lazy guarda a falha.
              chunkError
                ? window.location.reload()
                : this.setState({ error: null, componentStack: null, reloading: false })
            }
          >
            Tentar novamente
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}
